import React from "react";
import styles from "./SelectionCards.module.css";

interface Option {
  value: string;
  label: string;
  subtitle?: string;
}

interface SelectionCardsProps {
  options: Option[];
  selectedValue?: string;
  // TRAC-896: `origin` is the button element that was clicked, passed at click
  // time so a consumer that opens the vehicle picker can morph out of it.
  onSelect?: (value: string, label: string, origin?: HTMLElement | null) => void;
  disabled?: boolean;
}

// Pick a column count that fills rows evenly so the last row is never a lone stranded card
// (e.g. 5 → 3+2, not 4+1). Tire-model names are long, so we cap columns low and let the
// cards shrink to fit rather than packing as many per row as will fit.
const BALANCED_COLS = [0, 1, 2, 3, 2, 3, 3, 4, 4];
const balancedColumns = (n: number): number =>
  n <= 0 ? 1 : (BALANCED_COLS[n] ?? Math.min(4, Math.ceil(Math.sqrt(n))));

// Must match the `gap` on .cardsContainer (used to size each card's flex-basis so `cols`
// cards exactly fill a row).
const CARD_GAP_PX = 12;

/**
 * SelectionCards - Interactive card buttons for user selections
 *
 * Displays a grid of selectable cards. Clicking a card immediately
 * sends the selection (single-select only).
 *
 * Used for vehicle variants, tire sizes, preferences, etc.
 */
const SelectionCards: React.FC<SelectionCardsProps> = ({
  options,
  selectedValue,
  onSelect,
  disabled = false,
}) => {
  const handleClick = (option: Option, e: React.MouseEvent<HTMLButtonElement>): void => {
    if (disabled) return;
    if (onSelect) {
      onSelect(option.value, option.label, e.currentTarget);
    }
  };

  const isSelected = (value: string): boolean => {
    return value === selectedValue;
  };

  // Equal-width cards laid out with centered flex-wrap: full rows fill the width, and a
  // shorter LAST row (e.g. the 2 of a 3+2) centers within the margins of the rows above
  // instead of hanging left. `--card-basis` sizes each card so `cols` of them fill a row.
  const cols = balancedColumns(options.length);
  const containerStyle = {
    "--card-basis": `calc((100% - ${(cols - 1) * CARD_GAP_PX}px) / ${cols})`,
  } as React.CSSProperties;

  return (
    <div className={styles.selectionWrapper}>
      <div className={styles.cardsContainer} style={containerStyle}>
        {options.map((option, index) => {
          const selected = isSelected(option.value);
          const isDimmed = selectedValue && !selected;

          return (
            <button
              key={index}
              className={`${styles.card} ${
                selected ? styles.cardSelected : ""
              } ${isDimmed ? styles.cardDimmed : ""}`}
              disabled={disabled}
              onClick={(e) => handleClick(option, e)}
              aria-label={option.label}
              aria-pressed={selected}
              data-track-option={option.label}
            >
              <div className={styles.cardContent}>
                <div className={styles.cardLabel}>
                  {option.label.split(' ').map((word, wordIdx) => (
                    <React.Fragment key={wordIdx}>
                      {wordIdx > 0 && ' '}
                      {word.includes('/') ? (
                        word.split('/').map((part, partIdx, arr) => (
                          <React.Fragment key={partIdx}>
                            <span className={styles.nowrap}>
                              {part}{partIdx < arr.length - 1 ? '/' : ''}
                            </span>
                            {partIdx < arr.length - 1 && <wbr />}
                          </React.Fragment>
                        ))
                      ) : (
                        // plain words wrap/break naturally (long tire-model words like
                        // "ExtremeContact" must break rather than clip — see .cardLabel)
                        word
                      )}
                    </React.Fragment>
                  ))}
                </div>
                {option.subtitle && (
                  <div className={styles.cardSubtitle}>{option.subtitle}</div>
                )}
              </div>
              {selected && (
                <div className={styles.checkmark}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SelectionCards;
